<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      @click.self="closeModal"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full flex flex-col max-w-[90vw] h-[90vh]"
        role="dialog"
        aria-modal="true"
      >
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h3 class="text-lg font-semibold text-gray-900">{{ modalTitle }}</h3>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600"
            @click="closeModal"
          >
            <font-awesome-icon icon="xmark" class="text-xl" />
          </button>
        </div>

        <div class="px-6 py-4 overflow-auto flex-1">
          <form class="grid gap-6 h-full" style="grid-template-columns: 30% 1fr;" @submit.prevent="handleSubmit">
            <!-- Left Column: Agent Options -->
            <div class="space-y-6">
              <!-- Name -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  v-model="formData.name"
                  placeholder="Agent name"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-blue-dark focus:ring-blue-dark"
                />
              </div>

              <!-- Handoff Description -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Handoff Description</label>
                <textarea
                  v-model="formData.handoff_description"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark resize-none font-mono text-sm"
                  placeholder="Describe what this agent does when used as a handoff target..."
                  rows="3"
                />
                <p class="text-xs text-gray-500 mt-1">Used by other agents to know when to hand off to this agent</p>
              </div>

              <!-- Handoffs -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Handoffs ({{ selectedHandoffs.length }})</label>
                <p class="text-xs text-gray-500 mb-2">Select agents this agent can delegate to</p>
                <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded p-2">
                  <div
                    v-for="handoffAgent in availableAgents"
                    :key="handoffAgent.id"
                    class="flex items-center"
                  >
                    <label class="flex items-center cursor-pointer gap-2">
                      <div class="relative flex items-center">
                        <input
                          type="checkbox"
                          class="peer shrink-0 appearance-none w-3.5 h-3.5 border-2 border-gray-300 rounded bg-white checked:bg-primary-500 checked:border-orange-500 focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-0"
                          :checked="selectedHandoffs.includes(handoffAgent.id)"
                          @change="toggleHandoff(handoffAgent.id)"
                        />
                        <font-awesome-icon
                          icon="check"
                          class="absolute w-4 h-4 text-xs hidden peer-checked:block pointer-events-none text-white"
                        />
                      </div>
                      <span class="text-sm text-gray-700">{{ handoffAgent.name }}</span>
                    </label>
                  </div>
                  <div v-if="availableAgents.length === 0" class="text-sm text-gray-500 italic py-2">
                    No other agents available
                  </div>
                </div>
              </div>

              <!-- Tools -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Tools ({{ selectedTools.length }})</label>
                <p class="text-xs text-gray-500 mb-2">Select tools this agent can use</p>
                <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded p-2">
                  <div
                    v-for="tool in tools"
                    :key="tool.id"
                    class="flex items-center"
                  >
                    <label class="flex items-center cursor-pointer gap-2">
                      <div class="relative flex items-center">
                        <input
                          type="checkbox"
                          class="peer shrink-0 appearance-none w-3.5 h-3.5 border-2 border-gray-300 rounded bg-white checked:bg-primary-500 checked:border-orange-500 focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-0"
                          :checked="selectedTools.includes(tool.id)"
                          @change="toggleTool(tool.id)"
                        />
                        <font-awesome-icon
                          icon="check"
                          class="absolute w-4 h-4 text-xs hidden peer-checked:block pointer-events-none text-white"
                        />
                      </div>
                      <span class="text-sm text-gray-700">{{ tool.name }}</span>
                    </label>
                  </div>
                  <div v-if="tools.length === 0" class="text-sm text-gray-500 italic py-2">
                    No tools available
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  v-model="formData.status"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <!-- Right Column: Instructions -->
            <div class="flex flex-col">
              <label class="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
              <p class="text-xs text-gray-500 mb-2">System prompt that describes what the agent should do and how it responds</p>
              <textarea
                v-model="formData.instructions"
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark resize-none flex-1 font-mono text-sm"
                placeholder="Enter agent instructions (system prompt)..."
                required
              />
            </div>
          </form>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
          <div class="flex justify-between gap-3">
            <div>
              <span :class="['px-3 py-1.5 text-xs font-semibold rounded-full border', getStatusColorClass(formData.status)]">
                {{ formData.status.charAt(0).toUpperCase() + formData.status.slice(1) }}
              </span>
            </div>
            <div class="flex gap-3">
              <button
                type="button"
                class="font-semibold rounded-md transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-200 hover:bg-gray-300 text-gray-800 h-8 px-3 py-2 text-sm"
                @click="closeModal"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn-primary-sm"
                :disabled="isSaving"
                @click="handleSubmit"
              >
                <font-awesome-icon v-if="isSaving" icon="spinner" spin />
                {{ agent ? 'Update' : 'Create' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
const BADGE_COLORS = {
  success: 'bg-green-100 text-green-800 border-green-300',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  neutral: 'bg-gray-100 text-gray-800 border-gray-300',
}

const DEFAULT_AGENT = {
  name: '',
  instructions: '',
  handoff_description: '',
  status: 'draft',
}

export default {
  name: 'AgentModal',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    agent: {
      type: Object,
      default: null,
    },
    agents: {
      type: Array,
      required: true,
    },
    tools: {
      type: Array,
      required: true,
    },
  },
  emits: ['update:modelValue', 'save'],
  data() {
    return {
      isSaving: false,
      originalData: null,
      selectedHandoffs: [],
      selectedTools: [],
      formData: { ...DEFAULT_AGENT },
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    availableAgents() {
      return this.agents.filter(a => a.id !== this.formData.id)
    },
    modalTitle() {
      if (!this.agent) return 'New Agent'
      return this.formData.name || 'Edit Agent'
    },
  },
  watch: {
    agent: {
      handler(newAgent) {
        if (newAgent) {
          this.formData = {
            id: String(newAgent.id || ''),
            name: String(newAgent.name || ''),
            instructions: String(newAgent.instructions || ''),
            handoff_description: String(newAgent.handoff_description || ''),
            status: String(newAgent.status || 'draft'),
          }
          this.selectedHandoffs = newAgent.handoffs || []
          this.selectedTools = newAgent.tools || []
          this.originalData = { ...this.formData, handoffs: [...this.selectedHandoffs], tools: [...this.selectedTools] }
        } else {
          this.formData = { ...DEFAULT_AGENT }
          this.selectedHandoffs = []
          this.selectedTools = []
          this.originalData = { ...this.formData, handoffs: [], tools: [] }
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleHandoff(agentId) {
      const index = this.selectedHandoffs.indexOf(agentId)
      if (index > -1) {
        this.selectedHandoffs.splice(index, 1)
      } else {
        this.selectedHandoffs.push(agentId)
      }
    },
    toggleTool(toolId) {
      const index = this.selectedTools.indexOf(toolId)
      if (index > -1) {
        this.selectedTools.splice(index, 1)
      } else {
        this.selectedTools.push(toolId)
      }
    },
    closeModal() {
      if (this.originalData) {
        this.formData = { ...this.originalData }
        this.selectedHandoffs = [...(this.originalData.handoffs || [])]
        this.selectedTools = [...(this.originalData.tools || [])]
      }
      this.isOpen = false
    },
    async handleSubmit() {
      this.isSaving = true
      try {
        const dataToSave = {
          ...this.formData,
          handoffs: this.selectedHandoffs,
          tools: this.selectedTools,
        }
        this.$emit('save', dataToSave)
      } finally {
        this.isSaving = false
      }
    },
    getStatusColorClass(status) {
      const statusLower = (status || '').toLowerCase()
      const configColors = {
        published: BADGE_COLORS.success,
        draft: BADGE_COLORS.warning,
        archived: BADGE_COLORS.neutral,
      }
      return configColors[statusLower] || BADGE_COLORS.neutral
    },
  },
}
</script>
